import React from 'react';
import { cn } from './cn';
import { Icon } from './base/Icon';
import type { ClaimSource, QueueStatus } from './types';

/**
 * ป้ายสถานะของคิวงาน — ชุดสถานะปิดตาย ส่งสีเองไม่ได้
 * เหตุผล: ถ้าให้ส่งสีได้ สถานะเดียวกันจะมีสองสีในสองหน้า
 *
 * ทุกสถานะมีไอคอนบังคับและมีข้อความไทยกำกับ — ห้ามสื่อด้วยสีเพียงอย่างเดียว
 * (FR-ADM-10, 11, 14 · กติกาสีข้อ 5 ใน README)
 */

const STATUS: Record<QueueStatus, { label: string; icon: string; bg: string; text: string; border: string }> = {
  pending: {
    label: 'รอตรวจสอบ',
    icon: 'schedule',
    bg: 'bg-[var(--status-pending-bg)]',
    text: 'text-[var(--status-pending-text)]',
    border: 'border-[var(--status-pending-border)]',
  },
  review: {
    label: 'กำลังตรวจ',
    icon: 'person_search',
    bg: 'bg-[var(--status-review-bg)]',
    text: 'text-[var(--status-review-text)]',
    border: 'border-[var(--status-review-border)]',
  },
  approved: {
    label: 'อนุมัติแล้ว',
    icon: 'check_circle',
    bg: 'bg-[var(--status-approved-bg)]',
    text: 'text-[var(--status-approved-text)]',
    border: 'border-[var(--status-approved-border)]',
  },
  rejected: {
    label: 'ปฏิเสธ',
    icon: 'cancel',
    bg: 'bg-[var(--status-rejected-bg)]',
    text: 'text-[var(--status-rejected-text)]',
    border: 'border-[var(--status-rejected-border)]',
  },
  overdue: {
    label: 'หมดเวลา',
    icon: 'running_with_errors',
    bg: 'bg-[var(--status-overdue-bg)]',
    text: 'text-[var(--status-overdue-text)]',
    border: 'border-[var(--status-overdue-border)]',
  },
};

const SOURCE: Record<ClaimSource, { label: string; icon: string; bg: string; text: string }> = {
  auto: {
    label: 'ระบบตรวจอัตโนมัติ',
    icon: 'bolt',
    bg: 'bg-[var(--source-auto-bg)]',
    text: 'text-[var(--source-auto-text)]',
  },
  manual: {
    label: 'ต้องใช้คนตรวจ',
    icon: 'draw',
    bg: 'bg-[var(--source-manual-bg)]',
    text: 'text-[var(--source-manual-text)]',
  },
  failed: {
    label: 'ระบบตรวจไม่ได้',
    icon: 'sync_problem',
    bg: 'bg-[var(--source-failed-bg)]',
    text: 'text-[var(--source-failed-text)]',
  },
};

export interface StatusBadgeProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> {
  status: QueueStatus;
  /** ข้อความต่อท้าย เช่น 'เกิน 2 วัน' — ต่อจากชื่อสถานะ ไม่ใช่แทน */
  detail?: string;
  size?: 'sm' | 'md';
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({ status, detail, size = 'sm', className, ...rest }) => {
  const s = STATUS[status];
  return (
    <span
      {...rest}
      className={cn(
        'inline-flex max-w-full items-center gap-1 rounded-[var(--radius-pill)] border font-[family-name:var(--font-thai)] font-[var(--weight-medium)]',
        size === 'sm'
          ? 'px-2 py-[var(--space-05)] text-[length:var(--text-table-sm)] leading-[var(--leading-table)]'
          : 'px-2.5 py-[var(--space-1)] text-[length:var(--text-table)] leading-[var(--leading-table)]',
        s.bg,
        s.text,
        s.border,
        className,
      )}
    >
      <Icon name={s.icon} size={size === 'sm' ? 14 : 16} filled />
      <span className="bo-truncate">{s.label}{detail ? ` · ${detail}` : ''}</span>
    </span>
  );
};

export interface SourceBadgeProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> {
  source: ClaimSource;
}

/** ที่มาของคำขอ — แยกจากสถานะเพราะคำขอหนึ่งมีทั้งสองค่าพร้อมกัน */
export const SourceBadge: React.FC<SourceBadgeProps> = ({ source, className, ...rest }) => {
  const s = SOURCE[source];
  return (
    <span
      {...rest}
      className={cn(
        'inline-flex items-center gap-1 rounded-[var(--radius-sm)] px-2 py-[var(--space-05)] font-[family-name:var(--font-thai)] text-[length:var(--text-table-sm)] leading-[var(--leading-table)]',
        s.bg,
        s.text,
        className,
      )}
    >
      <Icon name={s.icon} size={14} />
      {s.label}
    </span>
  );
};

export const STATUS_LABEL: Record<QueueStatus, string> = {
  pending: STATUS.pending.label,
  review: STATUS.review.label,
  approved: STATUS.approved.label,
  rejected: STATUS.rejected.label,
  overdue: STATUS.overdue.label,
};

export const STATUS_ICON: Record<QueueStatus, string> = {
  pending: STATUS.pending.icon,
  review: STATUS.review.icon,
  approved: STATUS.approved.icon,
  rejected: STATUS.rejected.icon,
  overdue: STATUS.overdue.icon,
};
